<script setup lang="ts">
import SelectNew from "~/components/inputs/SelectNew.vue";
import creditFormRules from "~/rules/credit";
import { useVuelidate } from "@vuelidate/core";
import type { ITextIcon } from "~/interfaces";
import type { IValue } from "~/interfaces";
import Validator from "~/utils/validator";
import SlantedText from "~/components/static/SlantedText.vue";

defineComponent({
  name: "Credit",
});

const { icons, line1, line2 } = defineProps({
  ctaText: { type: String, default: "Continua", required: true },
  ctaSubtext: { type: String, default: "esito immediato", required: false },
  icons: { type: Array as () => ITextIcon[], required: false, default: [] },
  values: { type: Array as () => IValue[], default: () => [], required: true },
  value: { type: Number, required: false },
  line1: { type: String, required: false, default: "" },
  line2: { type: String, required: false, default: "" },

});

const emit = defineEmits(["submit"]);

const iconComponents = computed(() => {
  return icons.map((icon) => iconComponent(icon));
});

const credit = useState<number | null>("selectedCredit", () => null);

const v$ = useVuelidate(creditFormRules, {
  credit,
});

const creditSelect = useTemplateRef('credit-select')

const focusSelect = () => {
  creditSelect.value?.focus();
};

defineExpose({ creditSelect, focusSelect })

const validate = () => {
  v$.value.$touch();
  v$.value.$validate();
  return !v$.value.$error;
};

const submit = (e: Event) => {
  e.stopPropagation();
  e.preventDefault();

  if (validate()) {
    emit("submit");
  }
};
</script>

<template>
  <div
    id="main-content"
    class="credit-form md:container mt-44 md:mt-0 mx-auto text-center px-6 sm:px-10 md:px-6 relative"
  >
    <div class=" md:hidden ">
      <div class="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
        <SlantedText>
          <template #line1>{{ line1 }}</template>
          <template #line2>{{ line2 }}</template>
        </SlantedText>
      </div>
    </div>
    <div
      class="credit-container bg-white rounded-3xl shadow-2xl px-4 mx-auto max-w-4xl lg:max-w-5xl xl:max-w-7xl"
    >
      <h2 class="text-gray-5 font-semibold pt-14 sm:pt-10  px-4 text-2xl sm:px-8 lg:text-3xl">
        <slot name="title"></slot>
      </h2>
      <p class="mb-4">
        <slot name="subtitle"></slot>
      </p>
      <form @submit.prevent="(e) => submit(e)">
        <div class="max-w-md mx-auto">
          <SelectNew
            v-model="credit"
            class="w-full md:px-6 leading-normal rounded-full h-12 text-lg focus-within:shadow-white"
            disable-zero-value
            :values="values"
            :error="v$.credit.$error"
            :error-message="Validator.message(v$.credit)"
            ref="credit-select"
            error-margin
          />
          <button type="submit" class="home-submit">
            <template v-if="$slots.cta">
              <slot name="cta" />
            </template>

            <template v-else>
              <span class="block font-bold text-xl leading-5">{{
                ctaText
              }}</span>
              <span class="block leading-5 text-base">{{ ctaSubtext }}</span>
            </template>
          </button>
        </div>
      </form>
      
      <p class="mt-12 sm:mt-8 text-lg xs:text-xl text-gray-5 font-light">
        <slot name="text"></slot>
      </p>
      <template v-if="$slots.icons && icons.length === 0">
        <slot name="icons"></slot>
      </template>
      <div v-else class="flex justify-around items-center py-4 lg:px-40 pb-8">
        <template v-for="(el, index) in iconComponents" :key="index">
          <div class="flex flex-col md:flex-row items-center">
              <component :is="el.icon"></component>
              <h4 class="font-semibold text-sm md:ml-2" v-html="el.text"></h4>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped lang="css"></style>
